<template>
	<!-- ************************************************************************** Data table -->
	<PCard>
		<template #title>
			<div class="flex align-items-center justify-content-between">
				<WTitle text="Coefficients" />
				<div class="flex align-items-center gap-2">
					<!-- ************************************************************* Export current year to CSV -->
					<PButton
						:disabled="yearList.length === 0"
						@click="exportCoefficientsCsv()"
						icon="pi pi-download"
						outlined
						rounded
						severity="secondary"
						size="small"
						v-tooltip.bottom="`Export ${year} coefficients to CSV`"
					></PButton>
					<!-- ************************************************************* Generate (regression) -->
					<PButton
						v-if="canGenerate"
						@click="openGenerateDialog()"
						icon="pi pi-cog"
						label="Generate"
						rounded
						severity="help"
						size="small"
						v-tooltip.bottom="`Propose coefficients by regression over the prior 12 periods`"
					></PButton>
					<!-- ************************************************************** Add new button -->
					<PButton
						@click="showAddNewOrEdit = true"
						icon="pi pi-plus"
						rounded
						size="small"
						v-tooltip.left="`Add a new coefficient`"
					></PButton>
				</div>
			</div>
		</template>
		<template #content>
			<div class="my-3">
				<!-- Year selector -->
				<PFloatLabel>
					<PDropdown
						:options="years"
						@change="filterListByYear()"
						class="mx-3"
						id="coefficients-year"
						v-model="year"
					/>
					<label
						class="ml-1 font-bold"
						for="coefficients-year"
					>Year</label>
				</PFloatLabel>
			</div>
			<!-- Data table -->
			<PDataTable
				:alwaysShowPaginator="false"
				:highlightOnSelect="true"
				:loading="loading"
				:resizableColumns="true"
				:rowHover="true"
				:rows="100"
				:showGridlines="true"
				:sortOrder="1"
				:stripedRows="true"
				:value="yearList"
				data-key="id"
				paginator
				size="small"
				sortField="month_as_num"
			>
				<template #empty>No records found.</template>

				<PColumn
					bodyClass="text-center"
					class="justify-content-center"
					field="month_as_num"
					headerClass="py-3"
					style="width: 50px;"
				></PColumn>
				<PColumn
					bodyClass="text-left"
					class="justify-content-center"
					field="month"
					header="Month"
					headerClass="py-3"
					style="width: 120px;"
				></PColumn>
				<PColumn
					bodyClass="text-left text-wrap"
					field="ai"
					header="Ai"
					headerClass="py-3"
				></PColumn>
				<PColumn
					bodyClass="text-left text-wrap"
					field="bi"
					header="Bi"
					headerClass="py-3"
				></PColumn>
				<PColumn
					bodyClass="text-left text-wrap"
					field="ci"
					header="Ci"
					headerClass="py-3"
				></PColumn>
				<PColumn
					bodyClass="text-right"
					headerClass="py-3"
					style="width: 100px;"
				>
					<template #body="{ data }">
						<PButton
							@click="doEdit(data)"
							class="p-button-xs"
							icon="pi pi-pencil"
							rounded
							severity="success"
							text
							v-tooltip.bottom="`Edit`"
						></PButton>
						<PButton
							@click="doDelete($event, data.id)"
							icon="pi pi-trash"
							rounded
							severity="danger"
							size="small"
							text
							v-tooltip.bottom="`Delete`"
						></PButton>
					</template>
				</PColumn>
			</PDataTable>
		</template>
	</PCard>

	<!-- ************************************************************************** Regression proposals -->
	<PCard
		v-if="canGenerate || proposals.length"
		class="mt-4"
	>
		<template #title>
			<div class="flex align-items-center justify-content-between">
				<div class="flex flex-column gap-1">
					<WTitle text="Coefficient proposals" />
					<WSubTitle text="Plant-derived (regression) coefficients pending SPPC approval — Schedule 17 §3.13.2" />
				</div>
				<PButton
					:disabled="proposals.length === 0"
					@click="exportProposalsCsv()"
					icon="pi pi-download"
					label="Export"
					outlined
					rounded
					severity="secondary"
					size="small"
					v-tooltip.left="`Export proposals to CSV for SPPC submission`"
				></PButton>
			</div>
		</template>
		<template #content>
			<PDataTable
				:alwaysShowPaginator="false"
				:loading="proposalsLoading"
				:rowHover="true"
				:rows="20"
				:showGridlines="true"
				:stripedRows="true"
				:value="proposals"
				data-key="id"
				paginator
				size="small"
			>
				<template #empty>No proposals yet. Use Generate to propose coefficients.</template>

				<PColumn field="target_period" header="Target period" headerClass="py-3" sortable />
				<PColumn field="month" header="Month" headerClass="py-3" />
				<PColumn field="ai" header="Ai" headerClass="py-3" />
				<PColumn field="bi" header="Bi" headerClass="py-3" />
				<PColumn field="ci" header="Ci" headerClass="py-3" />
				<PColumn field="data_points" header="Data pts" headerClass="py-3" sortable />
				<PColumn field="status" header="Status" headerClass="py-3">
					<template #body="{ data }">
						<PTag :severity="proposalSeverity(data.status)">{{ data.status }}</PTag>
					</template>
				</PColumn>
				<PColumn field="generated_by" header="Generated by" headerClass="py-3" />
				<PColumn
					bodyClass="text-right"
					headerClass="py-3"
					style="width: 120px;"
				>
					<template #body="{ data }">
						<PButton
							v-if="canGenerate && data.status === 'proposed'"
							:loading="approvingId === data.id"
							@click="doApproveProposal(data)"
							icon="pi pi-check"
							label="Approve"
							severity="success"
							size="small"
							text
							v-tooltip.bottom="`Approve and promote to active coefficients`"
						></PButton>
						<span v-else-if="data.status === 'approved'" class="text-sm text-color-secondary">
							{{ data.approved_by || '—' }}
						</span>
					</template>
				</PColumn>
			</PDataTable>
		</template>
	</PCard>

	<!-- ************************************************************************** Generate dialog -->
	<PDialog
		:style="{ width: '32rem' }"
		header="Generate coefficients"
		@hide="cancelGenerate()"
		modal
		v-model:visible="showGenerate"
	>
		<div class="flex flex-column gap-4">
			<p class="text-sm text-color-secondary m-0">
				Fits a 2nd-order polynomial (A·GPOA² + B·GPOA + C) over valid hourly data from the
				prior 12 finalized billing periods, applying the Schedule 17 §3.13.2 filters
				(GPOA ≥ 100 W/m², positive total, MDL cap, valid quality). The result is saved as a
				proposal for SPPC approval — it does not change active coefficients until approved.
			</p>
			<div class="flex flex-column gap-2">
				<label class="text-primary-50" for="gen-period">Target billing period</label>
				<PDropdown
					:options="periodOptions"
					id="gen-period"
					optionLabel="label"
					optionValue="value"
					placeholder="Select billing period"
					v-model="generatePeriodId"
				/>
			</div>

			<!-- Result preview -->
			<div v-if="generateResult" class="generate-result">
				<div class="text-sm font-semibold mb-2">
					Proposed for {{ generateResult.target_period }} ({{ generateResult.month }})
				</div>
				<div class="flex gap-4 flex-wrap">
					<div><span class="text-color-secondary text-sm">Ai</span><div class="font-semibold">{{ generateResult.ai }}</div></div>
					<div><span class="text-color-secondary text-sm">Bi</span><div class="font-semibold">{{ generateResult.bi }}</div></div>
					<div><span class="text-color-secondary text-sm">Ci</span><div class="font-semibold">{{ generateResult.ci }}</div></div>
					<div><span class="text-color-secondary text-sm">Data points</span><div class="font-semibold">{{ generateResult.data_points }}</div></div>
				</div>
			</div>

			<div class="flex align-items-center justify-content-end gap-3">
				<PButton
					@click="cancelGenerate()"
					label="Close"
					outlined
					severity="secondary"
					size="small"
				></PButton>
				<PButton
					:disabled="!generatePeriodId"
					:loading="generating"
					@click="doGenerate()"
					label="Generate"
					size="small"
				></PButton>
			</div>
		</div>
	</PDialog>

	<!-- ************************************************************************** Add/Edit dialog -->
	<PDialog
		:header="`${editing? 'Edit' : 'Add a new'}`"
		@hide="cancelAddOrEdit()"
		modal
		v-model:visible="showAddNewOrEdit"
	>
		<div class="flex flex-column gap-4">
			<div class="flex flex-column gap-2">
				<label
					class="text-primary-50"
					for="year"
				>Year</label>
				<PInputText
					autocomplete="off"
					id="year"
					size="small"
					v-model="item.year"
				></PInputText>
			</div>
			<div class="flex flex-column gap-2">
				<label
					class="text-primary-50"
					for="month"
				>Month</label>
				<PInputText
					autocomplete="off"
					id="month"
					size="small"
					v-model="item.month"
				></PInputText>
			</div>
			<div class="flex flex-column gap-2">
				<label
					class="text-primary-50"
					for="ai"
				>Ai</label>
				<PInputNumber
					:useGrouping="false"
					autocomplete="off"
					id="ai"
					maxFractionDigits="50"
					minFractionDigits="0"
					mode="decimal"
					size="small"
					v-model="item.ai"
				></PInputNumber>
			</div>
			<div class="flex flex-column gap-2">
				<label
					class="text-primary-50"
					for="bi"
				>Bi</label>
				<PInputNumber
					:useGrouping="false"
					autocomplete="off"
					id="bi"
					maxFractionDigits="50"
					minFractionDigits="0"
					mode="decimal"
					size="small"
					v-model="item.bi"
				></PInputNumber>
			</div>
			<div class="flex flex-column gap-2">
				<label
					class="text-primary-50"
					for="ci"
				>Ci</label>
				<PInputNumber
					:useGrouping="false"
					autocomplete="off"
					id="ci"
					maxFractionDigits="50"
					minFractionDigits="0"
					mode="decimal"
					size="small"
					v-model="item.ci"
				></PInputNumber>
			</div>
			<div class="flex align-items-center justify-content-end gap-3">
				<PButton
					@click="cancelAddOrEdit()"
					label="Cancel"
					outlined
					severity="secondary"
					size="small"
				></PButton>
				<PButton
					@click="editing? saveUpdates() : doAdd()"
					label="Save"
					size="small"
				></PButton>
			</div>
		</div>
	</PDialog>

	<!-- ************************************************************************** Delete confirmation dialog -->
	<PConfirmDialog />
</template>

<script lang="ts">
	import { defineComponent, Events } from 'vue'
	import BEBroker from '@/brokers/BEBroker'
	import CoefficientsBroker, { CoefficientProposal, GenerateResult } from '@/brokers/CoefficientsBroker'
	import store from '@/store'
	import Utils from '@/utils/Utils'
	import Constants from '@/helpers/Constants'

	export default defineComponent({
		name: 'CoefficientsView',
		components: {
		},

		data() {
			return {
				utils: Utils,
				component: 'coefficients',
				loading: true,
				showAddNewOrEdit: false,
				editing: false,
				years: [] as Array<any>,
				year: '',
				list: [],
				yearList: [] as Array<any>,
				item: {
					id: -1,
					month: '',
					year: '',
					ai: 0,
					bi: 0,
					ci: 0,
				},
				// Phase 7 W-B — regression proposals.
				periods: [] as Array<any>,
				proposals: [] as CoefficientProposal[],
				proposalsLoading: false,
				showGenerate: false,
				generating: false,
				generatePeriodId: null as number | null,
				generateResult: null as GenerateResult | null,
				approvingId: null as number | null,
			}
		},

		computed: {
			canGenerate(): boolean {
				return store.getters.can('coefficients', 'generate')
			},
			periodOptions(): Array<{ label: string; value: number }> {
				return this.periods.map((p: any) => ({
					label: `${p.name}${p.is_trial ? ' (trial)' : ''} · ${p.status}`,
					value: p.id,
				}))
			},
		},


		async mounted() {
			// Get the current year
			const date = new Date()
			this.year = date.getFullYear().toString()

			Utils.generatePid(Constants.OPID.READ)
			await this.refreshList()
			if (this.canGenerate) {
				await this.refreshProposals()
			}
			this.loading = false
		},


		methods: {
			/**
			 * Refresh the items list
			 */
			async refreshList() {
				const res = await BEBroker.get(this.component)
				if (res != null && Object.keys(res).length !== 0) {
					this.list = res
				} else {
					this.list = []
				}
				this.prepareYears(this.list)
				this.filterListByYear()
			},

			prepareYears(list: Array<any>) {
				this.years = []
				for (const item of list) {
					if (!this.years.includes(item.year)) {
						this.years.push(item.year)
					}
				}
				this.year = this.years[0]
				// Order years in descending order
				this.years.sort((a, b) => a - b)
			},

			/**
			 * add a new item
			 */
			async doAdd() {
				// Validate input
				if (this.item.month.trim() === '' ||
					this.item.year.trim() === '' ||
					this.item.ai === 0 ||
					this.item.bi === 0 ||
					this.item.ci === 0) {
					Utils.showErrorMessage('All fields are required.')
					return
				}

				// Get month as number
				let monthAsNum = Constants.MONTH_TO_NUMBER.indexOf(this.item.month.toLowerCase())
				if (monthAsNum === -1) {
					Utils.showErrorMessage('Invalid month.')
					return
				}
				monthAsNum += 1

				this.loading = true
				Utils.generatePid(Constants.OPID.ADD)
				const payload = {
					month: this.item.month.trim(),
					year: this.item.year,
					ai: this.item.ai,
					bi: this.item.bi,
					ci: this.item.ci,
					month_as_num: monthAsNum,
				}
				const res = await BEBroker.add(this.component, payload)
				if (res) {
					this.showAddNewOrEdit = false
					this.refreshList()
					Utils.showSuccessMessage('Data has been added successfully.')
					Utils.clearObject(this.item)
				} else {
					Utils.showErrorMessage('Error while adding data!')
				}
				this.item.ai = 0
				this.item.bi = 0
				this.item.ci = 0
				this.loading = false
			},

			/**
			 * Show edit dialog
			 */
			doEdit(data: any) {
				this.editing = true
				this.item.id = data.id
				this.item.month = data.month
				this.item.year = data.year
				this.item.ai = data.ai
				this.item.bi = data.bi
				this.item.ci = data.ci
				this.showAddNewOrEdit = true
			},


			/**
			 * Close add or edit dialog and clear all fields
			 */
			cancelAddOrEdit() {
				this.showAddNewOrEdit = false
				this.editing = false
				Utils.clearObject(this.item)
				this.item.ai = 0
				this.item.bi = 0
				this.item.ci = 0
			},


			/**
			 * Save the updated record
			*/
			async saveUpdates() {
				// Validate input
				if (this.item.month.trim() === '' ||
					this.item.year.trim() === '' ||
					this.item.ai === 0 ||
					this.item.bi === 0 ||
					this.item.ci === 0) {
					Utils.showErrorMessage('All fields are required.')
					return
				}

				// Get month as number
				let monthAsNum = Constants.MONTH_TO_NUMBER.indexOf(this.item.month.toLowerCase())
				if (monthAsNum === -1) {
					Utils.showErrorMessage('Invalid month.')
					return
				}
				monthAsNum += 1

				this.loading = true
				Utils.generatePid(Constants.OPID.UPDATE)
				const payload = {
					id: this.item.id,
					month: this.item.month.trim(),
					year: this.item.year,
					ai: this.item.ai,
					bi: this.item.bi,
					ci: this.item.ci,
					month_as_num: monthAsNum
				}
				this.showAddNewOrEdit = false
				const res = await BEBroker.update(this.component, payload)
				this.editing = false
				Utils.clearObject(this.item)
				this.item.ai = 0
				this.item.bi = 0
				this.item.ci = 0
				this.refreshList()
				this.loading = false
				Utils.showSuccessMessage('Data has been updated successfully.')
			},


			/**
			 * Delete record
			 */
			async doDelete(event: any, id: number) {
				this.$confirm.require({
					target: event.currentTarget,
					header: 'Delete',
					message: 'Are you sure you want to delete this record?',
					icon: 'pi pi-info-circle',
					rejectClass: 'p-button-primary p-button-outlined p-button-sm',
					acceptClass: 'p-button-danger p-button-sm',
					rejectLabel: 'Cancel',
					acceptLabel: 'Delete',
					accept: async () => {
						this.loading = true
						Utils.generatePid(Constants.OPID.DELETE)
						const res = await BEBroker.delete(this.component, id)
						this.refreshList()
						this.loading = false
						Utils.showSuccessMessage('Data has been deleted successfully.')
					},
					reject: () => {
						// Do nothing
					}
				})
			},

			/**
			 * Update the list based on the selected year
			 */
			filterListByYear() {
				this.yearList = this.list.filter((item: any) => item.year === parseInt(this.year))
			},

			// ****************************************************************** Phase 7 W-B — regression

			proposalSeverity(status: string) {
				if (status === 'approved') return 'success'
				if (status === 'rejected') return 'danger'
				return 'info'
			},

			/**
			 * Load the coefficient proposals list.
			 */
			async refreshProposals() {
				this.proposalsLoading = true
				try {
					this.proposals = await CoefficientsBroker.listProposals()
				} catch (e: any) {
					Utils.showErrorMessage(e?.message || 'Could not load proposals.')
				} finally {
					this.proposalsLoading = false
				}
			},

			/**
			 * Open the Generate dialog (loads billing periods on first open).
			 */
			async openGenerateDialog() {
				this.generateResult = null
				this.generatePeriodId = null
				this.showGenerate = true
				if (this.periods.length === 0) {
					try {
						this.periods = await CoefficientsBroker.getBillingPeriods()
					} catch (e: any) {
						Utils.showErrorMessage(e?.message || 'Could not load billing periods.')
					}
				}
			},

			cancelGenerate() {
				this.showGenerate = false
				this.generateResult = null
				this.generatePeriodId = null
			},

			/**
			 * Run the regression for the selected target period.
			 */
			async doGenerate() {
				if (!this.generatePeriodId) return
				this.generating = true
				Utils.generatePid(Constants.OPID.ADD)
				try {
					this.generateResult = await CoefficientsBroker.generate(this.generatePeriodId)
					Utils.showSuccessMessage(
						`Proposed coefficients from ${this.generateResult.data_points} data points.`
					)
					await this.refreshProposals()
				} catch {
					// AxiosManager already toasts the server message (e.g. insufficient prior-period data).
				} finally {
					this.generating = false
				}
			},

			/**
			 * Approve a proposal and promote it to active coefficients.
			 */
			async doApproveProposal(data: CoefficientProposal) {
				this.approvingId = data.id
				Utils.generatePid(Constants.OPID.UPDATE)
				try {
					await CoefficientsBroker.approveProposal(data.id)
					Utils.showSuccessMessage('Proposal approved and coefficients updated.')
					await this.refreshProposals()
					await this.refreshList()
				} catch {
					// AxiosManager already toasts the server message.
				} finally {
					this.approvingId = null
				}
			},

			/**
			 * Export the current year's coefficients to a CSV file.
			 */
			exportCoefficientsCsv() {
				const headers = ['month', 'month_as_num', 'year', 'ai', 'bi', 'ci']
				const rows = this.yearList.map((r: any) =>
					[r.month, r.month_as_num, r.year, r.ai, r.bi, r.ci])
				this.downloadCsv(headers, rows, `coefficients-${this.year}.csv`)
			},

			/**
			 * Export the proposals list to a CSV file for SPPC submission.
			 */
			exportProposalsCsv() {
				const headers = ['target_period', 'month', 'ai', 'bi', 'ci',
					'data_points', 'status', 'generated_by', 'generated_at', 'source_periods']
				const rows = this.proposals.map((p: CoefficientProposal) =>
					[p.target_period, p.month, p.ai, p.bi, p.ci, p.data_points,
					 p.status, p.generated_by, p.generated_at, p.source_periods])
				this.downloadCsv(headers, rows, 'coefficient-proposals.csv')
			},

			/**
			 * Build a CSV blob and trigger a client-side download.
			 */
			downloadCsv(headers: string[], rows: any[][], filename: string) {
				const escape = (v: any) => {
					const s = v == null ? '' : String(v)
					return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
				}
				const lines = [headers.join(',')]
				for (const row of rows) {
					lines.push(row.map(escape).join(','))
				}
				const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
				const url = URL.createObjectURL(blob)
				const link = document.createElement('a')
				link.href = url
				link.download = filename
				link.click()
				URL.revokeObjectURL(url)
			},

		}
	})
</script>

<style scoped>
	.generate-result {
		border: 1px solid var(--surface-border);
		border-radius: 6px;
		padding: 0.75rem 1rem;
		background: var(--surface-ground);
	}
</style>
